import React, { useEffect, useRef } from 'react';
import { GxFeedItem } from '../../api/guestExperience';

// Extended feed item type with nested zone and reporter from Prisma relations
export interface FeedItemWithRelations extends GxFeedItem {
  zone?: { id: string; name: string; color: string };
  reporter?: { id: string; fullName: string };
  acknowledger?: { id: string; fullName: string };
}

export interface LiveFeedViewProps {
  items: FeedItemWithRelations[];
  onAcknowledge: (itemId: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
}

const MAX_DISPLAY_ITEMS = 100;

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  emergencySection: {
    marginBottom: '0.5rem',
  },
  feedCard: {
    padding: '0.75rem 1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    position: 'relative',
  },
  feedCardEmergency: {
    padding: '0.75rem 1rem',
    backgroundColor: '#ffebee',
    borderRadius: '8px',
    border: '2px solid #d32f2f',
    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
    position: 'relative',
  },
  feedCardAcknowledged: {
    padding: '0.75rem 1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    opacity: 0.7,
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.25rem',
    flexWrap: 'wrap' as const,
  },
  typeBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    padding: '2px 6px',
    borderRadius: '4px',
    color: '#fff',
  },
  zoneBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    color: '#fff',
  },
  message: {
    fontSize: '0.875rem',
    color: '#333',
    margin: '0.25rem 0',
    lineHeight: 1.4,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '0.25rem',
  },
  metaText: {
    fontSize: '0.75rem',
    color: '#999',
  },
  acknowledgeBtn: {
    fontSize: '0.75rem',
    padding: '4px 10px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  acknowledgedBadge: {
    fontSize: '0.7rem',
    color: '#4caf50',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  emptyFeed: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#999',
    fontSize: '0.875rem',
  },
  loadingMore: {
    textAlign: 'center' as const,
    padding: '1rem',
    color: '#999',
    fontSize: '0.8rem',
  },
  sentinel: {
    height: '1px',
  },
};

function getTypeBadgeColor(type: string): string {
  switch (type) {
    case 'EmergencyAlert':
      return '#d32f2f';
    case 'StandardAction':
      return '#1565c0';
    case 'Note':
    default:
      return '#666';
  }
}

function getTypeLabel(type: string): string {
  switch (type) {
    case 'EmergencyAlert':
      return '🚨 Emergency';
    case 'StandardAction':
      return '⚡ Action';
    case 'Note':
    default:
      return '📝 Note';
  }
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function LiveFeedView({ items, onAcknowledge, onLoadMore, hasMore }: LiveFeedViewProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver for lazy-loading
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, onLoadMore]);

  // Limit to max 100 items displayed
  const displayItems = items.slice(0, MAX_DISPLAY_ITEMS);

  // Separate emergency alerts (pinned at top) from regular items
  const emergencyAlerts = displayItems.filter(
    (item) => item.type === 'EmergencyAlert' && !item.acknowledged,
  );
  const regularItems = displayItems.filter(
    (item) => !(item.type === 'EmergencyAlert' && !item.acknowledged),
  );

  if (displayItems.length === 0) {
    return (
      <div style={styles.emptyFeed}>
        No feed items yet. Post a note or trigger an action to get started.
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Pinned emergency alerts at top */}
      {emergencyAlerts.length > 0 && (
        <div style={styles.emergencySection}>
          {emergencyAlerts.map((item) => (
            <FeedCard
              key={item.id}
              item={item}
              onAcknowledge={onAcknowledge}
            />
          ))}
        </div>
      )}

      {/* Regular feed items in reverse chronological order (already sorted) */}
      {regularItems.map((item) => (
        <FeedCard
          key={item.id}
          item={item}
          onAcknowledge={onAcknowledge}
        />
      ))}

      {/* Sentinel for IntersectionObserver lazy-loading */}
      {hasMore && (
        <>
          <div style={styles.loadingMore}>Loading more...</div>
          <div ref={sentinelRef} style={styles.sentinel} />
        </>
      )}
    </div>
  );
}

interface FeedCardProps {
  item: FeedItemWithRelations;
  onAcknowledge: (itemId: string) => void;
}

function FeedCard({ item, onAcknowledge }: FeedCardProps) {
  const isEmergency = item.type === 'EmergencyAlert';
  const isAcknowledged = item.acknowledged;

  const cardStyle = isEmergency
    ? styles.feedCardEmergency
    : isAcknowledged
      ? styles.feedCardAcknowledged
      : styles.feedCard;

  const zoneName = item.zone?.name || 'Unknown Zone';
  const zoneColor = item.zone?.color || '#999';
  const reporterName = item.reporter?.fullName || 'Unknown';

  return (
    <div style={cardStyle}>
      <div style={styles.cardHeader}>
        {/* Type badge */}
        <span
          style={{
            ...styles.typeBadge,
            backgroundColor: getTypeBadgeColor(item.type),
          }}
        >
          {getTypeLabel(item.type)}
        </span>

        {/* Zone badge with configured color */}
        <span
          style={{
            ...styles.zoneBadge,
            backgroundColor: zoneColor,
          }}
        >
          {zoneName}
        </span>
      </div>

      {/* Message */}
      <p style={styles.message}>{item.message}</p>

      {/* Meta: reporter, time, acknowledge button */}
      <div style={styles.meta}>
        <span style={styles.metaText}>
          {reporterName} • {formatTime(item.createdAt)}
        </span>

        {isAcknowledged ? (
          <span style={styles.acknowledgedBadge}>
            ✓ {item.acknowledger?.fullName || 'Acknowledged'}
          </span>
        ) : (
          <button
            style={styles.acknowledgeBtn}
            onClick={() => onAcknowledge(item.id)}
          >
            Acknowledge
          </button>
        )}
      </div>
    </div>
  );
}
